// @flow

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import type { Node } from 'react';
import type { WidgetData } from '@auravisionlabs/aura-pkg-report-widgets';
import {
  transformReportToMarkdown,
  generateSignature,
  createErrorWidgetData,
} from '../utils/report-markdown-transformer';

// Re-export for convenience
export type { WidgetData };

/**
 * Represents the state of a single widget in the report
 */
export type WidgetState = {
  id: string,
  status: 'registered' | 'loading' | 'complete' | 'error',
  data: WidgetData | null,
  error: string | null,
  // Signature of the report inputs (locations/periods/filters/etc.) that produced `data`.
  // Used to prevent stale widget data being included in markdown after the report inputs change.
  inputsSignature: string | null,
};

type ReportSpec = {
  title?: string,
  description?: string,
  period?: any,
  comparisonPeriod?: any,
  sections: Array<{
    sectionTitle?: string,
    period?: any,
    comparisonPeriod?: any,
    widgets: Array<any>,
  }>,
};

type Location = {
  id: string,
  name: string,
};

type ReportRenderContextValue = {
  // Widget registration
  registerWidget: (widgetId: string) => void,
  unregisterWidget: (widgetId: string) => void,

  // Widget status updates
  setWidgetLoading: (widgetId: string) => void,
  setWidgetComplete: (widgetId: string, data: WidgetData) => void,
  setWidgetError: (widgetId: string, error: string) => void,

  // Report state
  isReportComplete: boolean,
  widgetCount: number,
  completedWidgetCount: number,
  loadingWidgetCount: number,
  activeWidgetCount: number,

  // Markdown generation
  reportMarkdown: string | null,
  reportSignature: string | null,
  generateMarkdown: () => string | null,

  // AI chat support
  lastSentSignature: string | null,
  setLastSentSignature: (signature: string | null) => void,
  hasReportChanged: boolean,
  shouldSendMarkdown: boolean,
};

type ReportRenderActions = {
  registerWidget: (widgetId: string) => void,
  unregisterWidget: (widgetId: string) => void,
  setWidgetLoading: (widgetId: string) => void,
  setWidgetComplete: (widgetId: string, data: WidgetData) => void,
  setWidgetError: (widgetId: string, error: string) => void,
};

const ReportRenderContext = createContext<?ReportRenderContextValue>(null);
const ReportRenderActionsContext = createContext<?ReportRenderActions>(null);

/**
 * Returns the ReportRenderContext value, or null if not within a provider.
 * Components that require the context (like DownloadMdButton) should check for null.
 * The useWidgetRenderState hook handles null gracefully with no-op functions.
 */
export const useReportRenderContext = (): ?ReportRenderContextValue => {
  return useContext(ReportRenderContext);
};

// Widgets should prefer this to avoid re-rendering when only report state changes
export const useReportRenderActions = (): ?ReportRenderActions => {
  return useContext(ReportRenderActionsContext);
};

type ReportRenderProviderProps = {
  children: Node,
  reportSpec: ReportSpec,
  reportName: string,
  reportDescription?: string,
  locations: Location[],
  startOfWeek: 'monday' | 'sunday',
};

export const ReportRenderProvider = ({
  children,
  reportSpec,
  reportName,
  reportDescription,
  locations,
  startOfWeek,
}: ReportRenderProviderProps): Node => {
  const [widgetRegistry, setWidgetRegistry] = useState<
    Map<string, WidgetState>
  >(new Map());
  const [reportMarkdown, setReportMarkdown] = useState<string | null>(null);
  const [reportSignature, setReportSignature] = useState<string | null>(null);
  const [lastSentSignature, setLastSentSignature] = useState<string | null>(
    null
  );

  // Invalidate markdown/signature when markdown inputs change (locations/dates/config).
  // This prevents stale header/location/date strings if the user prompts AI quickly
  // after changing report filters, even if widgets have not yet triggered load events.
  // Signature of the report inputs relevant to widget data + markdown.
  // Includes effective locations, periods, comparison periods, and staff-exclusion filter.
  const reportInputsSignature = useMemo(() => {
    const normalizePeriod = (p: any) => {
      if (!p || !p.selectedDates) return null;
      return {
        selectedPreset: p.selectedPreset,
        selectedDates: {
          start: p.selectedDates.start,
          end: p.selectedDates.end,
        },
      };
    };

    const normalizeComparison = (cp: any) => {
      if (!cp) return null;
      return {
        active: cp.active,
        selectedPreset: cp.selectedPreset,
        selectedDates: cp.selectedDates
          ? { start: cp.selectedDates.start, end: cp.selectedDates.end }
          : null,
      };
    };

    const normalizeDemographicFilter = (df: any) => {
      if (!df) return null;
      // Typically used for "Exclude staff" -> { role: 'customer' }
      return df;
    };

    const normalizeQueryishConfig = (cfg: any) => {
      if (!cfg) return null;
      return {
        locations: cfg.locations,
        period: normalizePeriod(cfg.period),
        comparisonPeriod: normalizeComparison(cfg.comparisonPeriod),
        demographicFilter: normalizeDemographicFilter(cfg.demographicFilter),
        aggregation: cfg.aggregation,
        breakdownByDimensions: cfg.breakdownByDimensions,
        ages: cfg.ages,
        genders: cfg.genders,
        roles: cfg.roles,
        taxonomies: cfg.taxonomies,
        facets: cfg.facets,
        areaType: cfg.areaType,
        engagementAdditionalFilters: cfg.engagementAdditionalFilters,
        ignoreLingerUnder: cfg.ignoreLingerUnder,
        metric: cfg.metric,
        metrics: cfg.metrics,
        sortBy: cfg.sortBy,
        showChangeValue: cfg.showChangeValue,
      };
    };

    const normalizeWidgets = (widgets: any[] = []) =>
      widgets.map((w) => {
        const cfg = w && w.widgetConfig ? w.widgetConfig : w;
        return {
          widgetType: (w && w.widgetType) || (cfg && cfg.widgetType) || 'unknown',
          queryConfig: normalizeQueryishConfig(cfg),
        };
      });

    const locIds = (locations || [])
      .map((l) => l && l.id)
      .filter(Boolean)
      .slice()
      .sort();

    const summary = {
      startOfWeek,
      locationIds: locIds,
      reportPeriod: normalizePeriod(reportSpec && reportSpec.period),
      reportComparisonPeriod: normalizeComparison(
        reportSpec && reportSpec.comparisonPeriod
      ),
      reportDemographicFilter: normalizeDemographicFilter(
        reportSpec && reportSpec.demographicFilter
      ),
      sections: (reportSpec && reportSpec.sections
        ? reportSpec.sections
        : []
      ).map((s) => ({
        sectionTitle: s && s.sectionTitle,
        period: normalizePeriod(s && s.period),
        comparisonPeriod: normalizeComparison(s && s.comparisonPeriod),
        demographicFilter: normalizeDemographicFilter(s && s.demographicFilter),
        widgets: normalizeWidgets((s && s.widgets) || []),
      })),
    };

    return generateSignature(JSON.stringify(summary));
  }, [reportSpec, locations, startOfWeek]);

  useEffect(() => {
    // Clear markdown caches
    setReportMarkdown((prev) => (prev === null ? prev : null));
    setReportSignature((prev) => (prev === null ? prev : null));
    // Clear any stale widget data so regenerated markdown cannot include widgets
    // that haven't refreshed for the new report inputs.
    setWidgetRegistry((prev) => {
      if (prev.size === 0) return prev;

      let changed = false;
      const next = new Map(prev);

      next.forEach((w, id) => {
        const isStale = w.inputsSignature !== null && w.inputsSignature !== reportInputsSignature;
        const hasAnyData = w.data !== null || w.error !== null;
        const isActiveStatus = w.status !== 'registered';

        if (isStale || hasAnyData || isActiveStatus) {
          changed = true;
          next.set(id, {
            ...w,
            status: 'registered',
            data: null,
            error: null,
            inputsSignature: null,
          });
        }
      });

      return changed ? next : prev;
    });
  }, [reportInputsSignature]);

  // Computed values
  const widgetCount = widgetRegistry.size;
  
  const completedWidgetCount = useMemo(() => {
    return Array.from(widgetRegistry.values()).filter(
      (w) => w.status === 'complete' || w.status === 'error'
    ).length;
  }, [widgetRegistry]);

  const loadingWidgetCount = useMemo(() => {
    return Array.from(widgetRegistry.values()).filter(
      (w) => w.status === 'loading'
    ).length;
  }, [widgetRegistry]);

  // Widgets that have started loading (not just registered)
  const activeWidgetCount = useMemo(() => {
    return Array.from(widgetRegistry.values()).filter(
      (w) => w.status === 'loading' || w.status === 'complete' || w.status === 'error'
    ).length;
  }, [widgetRegistry]);

  // Report is ready for download when no widgets are actively loading
  const isReportComplete = activeWidgetCount > 0 && loadingWidgetCount === 0;
  const hasReportChanged =
    reportSignature !== null && reportSignature !== lastSentSignature;

  // Widget registration functions
  const registerWidget = useCallback((widgetId: string) => {
    setWidgetRegistry((prev) => {
      if (prev.has(widgetId)) {
        return prev;
      }

      const next = new Map(prev);
      next.set(widgetId, {
        id: widgetId,
        status: 'registered',
        data: null,
        error: null,
        inputsSignature: null,
      });
      return next;
    });
  }, []);

  const unregisterWidget = useCallback((widgetId: string) => {
    setWidgetRegistry((prev) => {
      if (!prev.has(widgetId)) {
        return prev;
      }

      const next = new Map(prev);
      next.delete(widgetId);
      return next;
    });
  }, []);

  const setWidgetLoading = useCallback((widgetId: string) => {
    setWidgetRegistry((prev) => {
      const widget = prev.get(widgetId);
      if (!widget) {
        return prev;
      }

      const isAlreadyLoading =
        widget.status === 'loading' && widget.data === null && widget.error === null;
      if (isAlreadyLoading) {
        return prev;
      }

      const next = new Map(prev);
      next.set(widgetId, {
        ...widget,
        status: 'loading',
        data: null,
        error: null,
        inputsSignature: reportInputsSignature,
      });
      return next;
    });
    // Invalidate markdown when any widget starts loading
    setReportMarkdown((prev) => (prev === null ? prev : null));
    setReportSignature((prev) => (prev === null ? prev : null));
  }, [reportInputsSignature]);

  const setWidgetComplete = useCallback(
    (widgetId: string, data: WidgetData) => {
      setWidgetRegistry((prev) => {
        const widget = prev.get(widgetId);
        if (!widget) {
          return prev;
        }

        const isAlreadyComplete =
          widget.status === 'complete' && widget.data === data && widget.error === null;
        if (isAlreadyComplete) {
          return prev;
        }

        const next = new Map(prev);
        next.set(widgetId, {
          ...widget,
          status: 'complete',
          data,
          error: null,
          inputsSignature: reportInputsSignature,
        });
        return next;
      });
    },
    [reportInputsSignature]
  );

  const setWidgetError = useCallback((widgetId: string, error: string) => {
    setWidgetRegistry((prev) => {
      const widget = prev.get(widgetId);
      if (!widget) {
        return prev;
      }

      const errorData = createErrorWidgetData(widgetId, error);
      const isAlreadyErrored =
        widget.status === 'error' && widget.error === error;
      if (isAlreadyErrored) {
        return prev;
      }

      const next = new Map(prev);
      next.set(widgetId, {
        ...widget,
        status: 'error',
        data: errorData,
        error,
        inputsSignature: reportInputsSignature,
      });
      return next;
    });
  }, [reportInputsSignature]);

  // Markdown generation (called on demand)
  const generateMarkdown = useCallback(() => {
    if (!isReportComplete) return null;

    // Enrich reportSpec with name and description
    const enrichedSpec = {
      ...reportSpec,
      title: reportName,
      description: reportDescription,
    };

    const markdown = transformReportToMarkdown(
      enrichedSpec,
      widgetRegistry,
      locations,
      startOfWeek,
      reportInputsSignature
    );

    const signature = generateSignature(markdown);

    setReportMarkdown(markdown);
    setReportSignature(signature);

    return markdown;
  }, [isReportComplete, reportSpec, reportName, reportDescription, widgetRegistry, locations, startOfWeek, reportInputsSignature]);

  // Determines if markdown should be sent with the next prompt
  const shouldSendMarkdown = lastSentSignature === null || hasReportChanged;

  const value = useMemo(
    () => ({
      registerWidget,
      unregisterWidget,
      setWidgetLoading,
      setWidgetComplete,
      setWidgetError,
      isReportComplete,
      widgetCount,
      completedWidgetCount,
      loadingWidgetCount,
      activeWidgetCount,
      reportMarkdown,
      reportSignature,
      generateMarkdown,
      lastSentSignature,
      setLastSentSignature,
      hasReportChanged,
      shouldSendMarkdown,
    }),
    [
      registerWidget,
      unregisterWidget,
      setWidgetLoading,
      setWidgetComplete,
      setWidgetError,
      isReportComplete,
      widgetCount,
      completedWidgetCount,
      loadingWidgetCount,
      activeWidgetCount,
      reportMarkdown,
      reportSignature,
      generateMarkdown,
      lastSentSignature,
      hasReportChanged,
      shouldSendMarkdown,
    ]
  );

  const actions = useMemo(
    () => ({
      registerWidget,
      unregisterWidget,
      setWidgetLoading,
      setWidgetComplete,
      setWidgetError,
    }),
    [
      registerWidget,
      unregisterWidget,
      setWidgetLoading,
      setWidgetComplete,
      setWidgetError,
    ]
  );

  return (
    <ReportRenderActionsContext.Provider value={actions}>
      <ReportRenderContext.Provider value={value}>
        {children}
      </ReportRenderContext.Provider>
    </ReportRenderActionsContext.Provider>
  );
};
